import { type PropertyInterestData } from '@aikoa/common'
import { useCurrentLanguage, useTranslations } from '@aikoa/i18n/react'
import { useMemo, type ReactNode } from 'react'
import { View, type ViewProps } from 'react-native'
import Animated, { FadeIn } from 'react-native-reanimated'
import { InterestDescription } from '~/components/interests/InterestDescription'
import { formatPrice, formatRange, formatRoomsRange } from '~/components/interests/utils'
import { ValueList, type ValueItem } from '~/components/layout/ValueList'
import { Label } from '~/components/typography/Content'
import { getBuildingTypeLabel, getConditionLabel } from '~/lib/assets'
import { cn } from '~/lib/utils'

export interface InterestDetailsViewProps extends ViewProps {
  interest: PropertyInterestData
  valueClassName?: string
  valueChildren?: {
    price?: ReactNode
  }
}

export function InterestDetailsView({
  interest,
  className,
  valueClassName,
  valueChildren,
  ...props
}: InterestDetailsViewProps) {
  const { t } = useTranslations('interest')
  const { t: tProperty } = useTranslations('property')
  const language = useCurrentLanguage()

  const { filters, locations } = interest

  const values = useMemo(() => {
    const values: ValueItem[] = []

    if (locations && locations.length > 0) {
      values.push({
        icon: 'location-outline',
        value: t(($) => $.labels.locations),
        description: <ChipList items={locations.map((l) => l.label)} />,
      })
    }

    if (filters?.buildingTypes && filters.buildingTypes.length > 0) {
      values.push({
        icon: 'home-outline',
        value: t(($) => $.labels.buildingTypes),
        description: (
          <ChipList
            items={filters.buildingTypes.map((type) =>
              getBuildingTypeLabel({ buildingType: type, t: tProperty })
            )}
          />
        ),
      })
    }

    if (filters?.priceMin !== undefined || filters?.priceMax !== undefined) {
      values.push({
        icon: 'pricetag-outline',
        value: t(($) => $.labels.price),
        description: formatPrice(language, t, filters?.priceMin, filters?.priceMax),
        children: valueChildren?.price,
      })
    }
    if (filters?.sizeMin !== undefined || filters?.sizeMax !== undefined) {
      values.push({
        icon: 'expand-outline',
        value: t(($) => $.labels.size),
        description: formatRange(filters?.sizeMin, filters?.sizeMax, t(($) => $.details.sizeUnit)),
      })
    }

    if (filters?.roomsMin !== undefined || filters?.roomsMax !== undefined) {
      values.push({
        icon: 'bed-outline',
        value: t(($) => $.labels.rooms),
        description: formatRoomsRange(filters?.roomsMin, filters?.roomsMax, t),
      })
    }

    if (filters?.buildYearMin !== undefined || filters?.buildYearMax !== undefined) {
      values.push({
        icon: 'calendar-outline',
        value: t(($) => $.labels.buildYear),
        description: formatRange(filters?.buildYearMin, filters?.buildYearMax, ''),
      })
    }

    if (filters?.conditions && filters.conditions.length > 0) {
      values.push({
        icon: 'construct-outline',
        value: t(($) => $.labels.condition),
        description: (
          <ChipList items={filters.conditions.map((c) => getConditionLabel(c, tProperty))} />
        ),
      })
    }

    const trimmedDescription =
      (interest?.description ?? '') ? String(interest?.description ?? '').trim() : ''

    if (trimmedDescription.length > 0) {
      values.push({
        icon: 'document-text-outline',
        value: t(($) => $.labels.description),
        showLabel: false,
        description: (
          <InterestDescription
            description={trimmedDescription}
            textClassName={cn('font-light text-lg/relaxed')}
            labelClassName={cn('font-semibold text-lg/relaxed capitalize no-underline')}
          />
        ),
      })
    }

    return values
  }, [filters, interest, language, locations, t, tProperty, valueChildren?.price])

  return (
    <Animated.View className={cn(className)} entering={FadeIn.duration(150)} {...props}>
      <ValueList
        values={values}
        className={cn('flex-1 overflow-hidden')}
        itemClassName={cn('text-pretty last:border-b-0', valueClassName)}
      />
    </Animated.View>
  )
}

function ChipList({ items }: { items: string[] }) {
  return (
    <View className="flex-row flex-wrap gap-2 pt-1">
      {items.map((label) => (
        <View
          key={label}
          className="rounded-full border border-neutral-300 bg-neutral-200 px-4 py-1">
          <Label className="select-none font-normal text-sm text-foreground">{label}</Label>
        </View>
      ))}
    </View>
  )
}
